"use client";
/**
 * The ball: porcelain glass with Bash inside (spec 2026-09-12 §2 to §6; mocks
 * of record mockups/composer-ball-life-2026-09-12.html and
 * mockups/composer-line-work-2026-09-12.html, the founder's picks). Every
 * layer is light on one small span (globals.css, the `cl-` section): the page
 * never changes colour, the ball is the one object allowed gradients, and
 * each light is its own layer so a tone change only moves opacity. One light
 * for every working state (gold), copper only while Bash speaks. The face is
 * the shipped BashFace; its mouth talks through `data-talk` (bash-home.css),
 * written per frame from the band the talk driver fills. Nothing here
 * re-renders per frame: your energy and Bash's ride CSS variables on the
 * button. The work of a spoken turn lives in the glass: one glyph for the
 * current step (keyed per step so the same icon twice still re-enters), held
 * just below the face, which lifts while `data-hold` is on the orb; the
 * flash and the spark of a birth (keyed per birth), the face's pop and nod
 * (an act wrapper whose parity flips so a second one replays), and the
 * birth's settle on the wrapper's own `data-birth`, which outlives the nod.
 *
 * At rest (tone none, the line off) the ball costs nothing but its breath:
 * the frame loop runs only while a tone is on.
 */
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { BashFace, type FaceMood } from "@/components/bash-home/BashFace";

export type BallTone = "none" | "gold" | "copper";
export type BallGlyph = "search" | "read" | "create" | "check" | "nami";
export type TalkBand = 0 | 1 | 2 | 3;

export interface BallProps {
  /** The ball's diameter in px; every layer scales from it. */
  size?: number;
  mood: FaceMood;
  /** The light law: gold is you (and every working state), copper is Bash. */
  tone: BallTone;
  /** Your voice, 0..1, smoothed by the driver. */
  energyRef?: { current: number };
  /** Bash's voice, 0..1, smoothed by the driver. */
  bashRef?: { current: number };
  /** The mouth: 0 rest, 1 a small o, 2 open, 3 wide with the jaw dropped. */
  talkRef?: { current: TalkBand };
  pressed?: boolean;
  onClick?: () => void;
  className?: string;
  label?: string;
  /** One glyph in the glass for the current step; null for none. */
  glyph?: BallGlyph | null;
  /** Bumped per step by the stage, so the same icon twice still re-enters. */
  glyphKey?: number;
  /** Bumped per task birth: the flash, the spark and the face pop replay. */
  sparkKey?: number;
  /** The nod as the receipt settles. */
  nod?: boolean;
}

type Act = "pop-a" | "pop-b" | "nod-a" | "nod-b";
/** The birth's settle, bash-home.css's 1.4 s, and a beat of grace so the
 *  clear never lands on the settle's last frame. */
const BIRTH_MS = 1500;

const unit = (n: number) => Math.min(1, Math.max(0, n)).toFixed(3);

/** The six icons, the verbs' mock paths (24-unit box;
 *  mockups/composer-bash-verbs-2026-09-12.html, treatment C). The stroke
 *  classes are what globals.css animates: `mg` sweeps, `ln1`/`ln2` and
 *  `pv`/`ph` draw themselves, the mark's `nring`/`ndot`/`narc` pulse. */
function GlyphIcon({ glyph }: { glyph: BallGlyph }) {
  switch (glyph) {
    case "search":
      return (
        <svg viewBox="0 0 24 24" aria-hidden>
          <g className="mg">
            <circle cx="10.5" cy="10.5" r="6" />
            <path d="M15.5 15.5 20 20" />
          </g>
        </svg>
      );
    case "read":
      return (
        <svg viewBox="0 0 24 24" aria-hidden>
          <path d="M7 3h7l5 5v13H7z" />
          <path d="M14 3v5h5" />
          <path className="ln ln1" d="M10 13h5.5" />
          <path className="ln ln2" d="M10 16.5h5.5" />
        </svg>
      );
    case "create":
      return (
        <svg viewBox="0 0 24 24" aria-hidden>
          <path className="pv" d="M12 5v14" />
          <path className="ph" d="M5 12h14" />
        </svg>
      );
    case "nami":
      return (
        <svg viewBox="0 0 24 24" aria-hidden>
          <circle className="nring" cx="12" cy="12" r="6.4" />
          <circle className="ndot" cx="12" cy="12" r="2.1" />
          <path className="narc" d="M12 2.8a9.2 9.2 0 0 1 6.8 3" />
          <path className="narc" d="M12 21.2a9.2 9.2 0 0 1-6.8-3" />
        </svg>
      );
    case "check":
      return (
        <svg viewBox="0 0 24 24" aria-hidden>
          <path d="M5 12.5l4.5 4.5L19 7.5" />
        </svg>
      );
  }
}

export function Ball({
  size = 128,
  mood,
  tone,
  energyRef,
  bashRef,
  talkRef,
  pressed,
  onClick,
  className,
  label = "Open the line",
  glyph = null,
  glyphKey = 0,
  sparkKey = 0,
  nod = false,
}: BallProps) {
  const rootRef = useRef<HTMLButtonElement>(null);
  const faceRef = useRef<HTMLSpanElement>(null);
  const live = tone !== "none";

  // The act on the face: a birth pops it, a settled receipt nods it. Each
  // act counts its own plays and alternates parity, so the same act twice
  // replays even when a birth's key jumps by two (a coalesced double). Only
  // a change after mount acts: a remount carrying a stale key or a held nod
  // (the stage-to-mark swap as the type door opens) stays still, and a nod
  // ending never replays the pop (the act stays the last one played).
  // The birth's 1.4 s settle (bash-home.css's clActBirth rules) rides its
  // own attribute on the same wrapper, parity-flipped with the pop: the nod
  // takes data-act the moment `done: routed` lands, right after the action,
  // and a settle keyed there was cut mid-play. It clears itself once the
  // settle has run; left in place, the next return to a mood its rules
  // dress would replay it.
  const [act, setAct] = useState<Act | null>(null);
  const [birth, setBirth] = useState<"a" | "b" | null>(null);
  const pops = useRef(0);
  const nods = useRef(0);
  const lastSpark = useRef(sparkKey);
  const lastNod = useRef(nod);
  useEffect(() => {
    if (sparkKey === lastSpark.current) return;
    lastSpark.current = sparkKey;
    if (sparkKey === 0) return;
    pops.current += 1;
    setAct(pops.current % 2 ? "pop-a" : "pop-b");
    setBirth(pops.current % 2 ? "a" : "b");
    const t = setTimeout(() => setBirth(null), BIRTH_MS);
    return () => clearTimeout(t);
  }, [sparkKey]);
  useEffect(() => {
    if (nod === lastNod.current) return;
    lastNod.current = nod;
    if (!nod) return;
    nods.current += 1;
    setAct(nods.current % 2 ? "nod-a" : "nod-b");
  }, [nod]);

  useEffect(() => {
    if (!live || (!energyRef && !bashRef && !talkRef)) return;
    let raf = 0;
    let lastEnergy = "";
    let lastBash = "";
    let lastTalk = "";
    const loop = () => {
      const root = rootRef.current;
      if (root && energyRef) {
        const v = unit(energyRef.current);
        if (v !== lastEnergy) {
          lastEnergy = v;
          root.style.setProperty("--cl-energy", v);
        }
      }
      if (root && bashRef) {
        const v = unit(bashRef.current);
        if (v !== lastBash) {
          lastBash = v;
          root.style.setProperty("--cl-bash", v);
        }
      }
      if (talkRef) {
        const face = faceRef.current?.querySelector<HTMLElement>(".bh-face");
        const v = String(talkRef.current);
        if (face && v !== lastTalk) {
          lastTalk = v;
          face.setAttribute("data-talk", v);
        }
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      // The off edge: whatever the last frame wrote must not outlive the
      // line (a tilted face, an open mouth, at rest). Rest is the
      // stylesheet's own, so the inline values go rather than being written
      // to zero.
      const root = rootRef.current;
      root?.style.removeProperty("--cl-energy");
      root?.style.removeProperty("--cl-bash");
      faceRef.current?.querySelector(".bh-face")?.removeAttribute("data-talk");
    };
  }, [energyRef, bashRef, talkRef, live]);

  return (
    <button
      ref={rootRef}
      type="button"
      className={cn(
        "cl-orb relative block shrink-0 cursor-pointer select-none rounded-full border-0 bg-transparent p-0 focus-visible:outline-2 focus-visible:outline-offset-8 focus-visible:outline-cv2-bar",
        pressed && "cl-orb--pressed",
        className
      )}
      data-tone={tone}
      data-mood={mood}
      data-glyph={glyph ? glyph : undefined}
      data-hold={glyph ? "" : undefined}
      style={{ "--cl-size": `${size}px` } as React.CSSProperties}
      aria-label={label}
      onClick={onClick}
    >
      {/* the light on the porcelain around and beneath the ball */}
      <span className="cl-orb__halo cl-orb__halo--idle" aria-hidden />
      <span className="cl-orb__halo cl-orb__halo--idle2" aria-hidden />
      <span className="cl-orb__halo cl-orb__halo--au" aria-hidden />
      <span className="cl-orb__halo cl-orb__halo--cu" aria-hidden />
      <span className="cl-orb__halo cl-orb__halo--nt" aria-hidden />
      <span className="cl-orb__shimmer" aria-hidden />
      <span className="cl-orb__leak cl-orb__leak--au" aria-hidden />
      <span className="cl-orb__leak cl-orb__leak--cu" aria-hidden />
      <span className="cl-orb__contact" aria-hidden />
      <span className="cl-orb__contact2" aria-hidden />
      {/* the kinetic wrapper (hover lift, press, the spring wake) > the breath */}
      <span className="cl-orb__k" aria-hidden>
        <span className="cl-orb__b">
          <span className="cl-orb__body" />
          {/* the inner light, masked to the sphere */}
          <span className="cl-orb__in">
            <span className="cl-orb__caustic" />
            <span className="cl-orb__rise" />
            <span className="cl-orb__tide" />
            <span className="cl-orb__pool cl-orb__pool--cu" />
            <span className="cl-orb__poolwrap">
              <span className="cl-orb__pool cl-orb__pool--au" />
            </span>
            <span className="cl-orb__brew" />
            <span className="cl-orb__brew cl-orb__brew--two" />
            <span className="cl-orb__bright" />
          </span>
          {/* Bash inside: whole, floating, acting; the act wrapper pops and nods, and carries the birth's settle */}
          <span className="cl-orb__float">
            <span
              className="cl-orb__act"
              data-act={act ?? undefined}
              data-birth={birth ?? undefined}
            >
              <span className="cl-orb__face" ref={faceRef}>
                <BashFace size="big" mood={mood} />
              </span>
            </span>
          </span>
          {/* the work in the glass: the flash and the spark of a birth, one glyph for the step */}
          <span className="cl-orb__work">
            {sparkKey > 0 && (
              <span key={`f${sparkKey}`} className="cl-orb__flash" />
            )}
            {glyph && (
              <span
                key={`${glyphKey}-${glyph}`}
                className="cl-orb__glyph"
                data-g={glyph}
              >
                <span className="cl-orb__glyph-d">
                  <span className="cl-orb__glyph-i">
                    <GlyphIcon glyph={glyph} />
                  </span>
                </span>
              </span>
            )}
            {sparkKey > 0 && (
              <span key={`s${sparkKey}`} className="cl-orb__spark">
                <svg viewBox="0 0 24 24" aria-hidden>
                  <path d="M12 2C12.7 8.2 15.8 11.3 22 12c-6.2.7-9.3 3.8-10 10-.7-6.2-3.8-9.3-10-10 6.2-.7 9.3-3.8 10-10z" />
                </svg>
              </span>
            )}
          </span>
          {/* the glass: the refracting rim, the environment arc, the speculars */}
          <span className="cl-orb__fresnel" />
          <span className="cl-orb__env" />
          <span className="cl-orb__spec" />
          <span className="cl-orb__spec2" />
          <span className="cl-orb__ring cl-orb__ring--au" />
          <span className="cl-orb__ring cl-orb__ring--cu" />
          <span className="cl-orb__ring cl-orb__ring--nt" />
        </span>
      </span>
    </button>
  );
}
